import React, { useEffect, useState, useContext } from 'react';
import axios from "axios";
import { useTranslation } from "react-i18next";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import '../pages.css';
import {
    MInputTextField,
    MButton,
    DefaultURl,
    LoadingContext,
    M_StateActions,
    MCheckBox
} from 'src/M_Components';
import { MHeader } from 'src/M_Components/M_Header';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { apiMessage, isFailurePayload } from 'src/components/utils/apiResponse';

//region Constants

/**
 * Permission columns rendered in the profile permission grid.
 *
 * Each value must match the boolean field returned by the backend PermissionDTO.
 */
const PERMISSION_COLUMNS = [
    "CanList",
    "CanCreate",
    "CanUpdate",
    "CanDelete",
    "CanRead",
    "IsAdmin"
];

//endregion

//region Component

/**
 * Renders the Role/Profile CRUD page.
 *
 * Responsibilities:
 * - load profile permissions in grid format;
 * - support Create, Edit, Copy, and View modes;
 * - show translated module names in the permission grid;
 * - allow permission editing only in Create, Edit, and Copy modes;
 * - prevent save actions in View mode;
 * - display API validation messages returned by the backend;
 * - persist tab-local state through M_State.
 *
 * @param {object} props - Component props.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @param {Function} props.handleTabClose - Closes the current application tab.
 * @returns {JSX.Element} Role/Profile CRUD page.
 */
export default function RoleConfigCrud({ toastMessages, handleTabClose }) {
    const dispatch = useDispatch();
    const M_State = useSelector(state => state.StateKeyValue);
    const ctx = useContext(LoadingContext);
    const { t } = useTranslation();

    const passOperation =
        M_State.filter(obj => obj.key === 'operation')[0]?.value !== null &&
            M_State.filter(obj => obj.key === 'operation')[0]?.value !== undefined
            ? M_State.filter(obj => obj.key === 'operation')[0]?.value
            : 'Error';

    const sessionLine =
        M_State.filter(obj => obj.key === 'rolesLine')[0]?.value !== null &&
            M_State.filter(obj => obj.key === 'rolesLine')[0]?.value !== undefined
            ? M_State.filter(obj => obj.key === 'rolesLine')[0]?.value
            : 'Error';

    const [id, setId] = useState(M_State.filter(obj => obj.key === 'selectedTab')[0]?.value);
    const [roleName, setRoleName] = useState('');
    const [permissions, setPermissions] = useState([]);
    const [operation, setOperation] = useState(passOperation);
    const [passLine, setPassLine] = useState('');
    const [isFirst, setIsFirst] = useState(true);

    const isViewMode = (operation || '').toLowerCase() === 'view';
    const isCreateMode = operation === 'Create';
    const isCopyMode = operation === 'Copy';
    const isEditMode = operation === 'Edit';

    const sessionKeys = ['operation', 'roleName', 'permissions', 'passLine', 'isFirst'];

    //region Session helpers

    /**
     * Returns the values that must be persisted for the current tab.
     *
     * @returns {Array} Current tab state values.
     */
    function getSessionKeys() {
        return [
            operation,
            roleName,
            permissions,
            passLine,
            isFirst
        ];
    }

    /**
     * Builds an M_State-compatible key/value entry.
     *
     * @param {string|number} id - Current tab identifier.
     * @param {string} key - State entry key.
     * @param {*} value - State entry value.
     * @returns {object} State entry object.
     */
    const sessionStorageMachine = (id, key, value) => {
        let tempObj = {};

        if (value instanceof Map) {
            tempObj = { key: `${key}`, value: Array.from(value) };
        } else {
            tempObj = { key: `${key}`, value };
        }

        return tempObj;
    };

    /**
     * Restores the current tab state from M_State.
     *
     * When no tab snapshot exists, profile data is loaded from the backend.
     *
     * @param {string|number} sessionID - Current tab identifier.
     * @returns {void}
     */
    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true);

        const M_StateData = M_State.filter(obj => obj.key === sessionID)[0]?.value;

        if (M_StateData) {
            const tempO = M_StateData.filter(obj => obj?.key === 'operation')[0]?.value;
            const tempRN = M_StateData.filter(obj => obj?.key === 'roleName')[0]?.value;
            const tempP = M_StateData.filter(obj => obj?.key === 'permissions')[0]?.value;
            const tempL = M_StateData.filter(obj => obj?.key === 'passLine')[0]?.value;
            const tempIF = M_StateData.filter(obj => obj?.key === 'isFirst')[0]?.value;

            if (tempO || tempRN || tempP || tempL || tempIF) {
                setOperation(tempO);
                setRoleName(tempRN ?? '');
                setPermissions(tempP ?? []);
                setPassLine(tempL);
                setIsFirst(tempIF);
                ctx.setIsLoad(false);
                return;
            }
        }

        getRefresh(sessionLine);
    };

    //endregion

    //region API response helpers

    /**
     * Resolves the payload returned by the backend.
     *
     * Some endpoints return the payload directly, while others return an API response
     * wrapper with Data/data. This helper keeps the component compatible with both shapes.
     *
     * @param {*} payload - Raw response payload.
     * @returns {*} Resolved data payload.
     */
    const getApiData = (payload) => {
        return payload?.Data ?? payload?.data ?? payload;
    };

    /**
     * Displays an API error message using the shared API response helper.
     *
     * This keeps validation messages returned by the backend visible to the user instead
     * of showing generic Axios errors such as request status messages.
     *
     * @param {*} error - API response payload or Axios error.
     * @param {string} fallbackKey - Translation key used as fallback message.
     * @returns {void}
     */
    const showApiError = (error, fallbackKey = 'Error') => {
        toastMessages(
            'error',
            '',
            apiMessage(error, t(fallbackKey))
        );
    };

    //endregion

    //region Effects

    /**
     * Restores initial state when the page is mounted.
     *
     * The one-shot global row and operation values are copied into tab-local state
     * and then removed from the global state store.
     */
    useEffect(() => {
        if (isFirst) {
            dispatch(M_StateActions.removeM_State({ key: 'rolesLine' }));
            dispatch(M_StateActions.removeM_State({ key: 'operation' }));
            setPassLine(sessionLine);
            setIsFirst(false);
        }

        getSessionStorageData(id);
        // eslint-disable-next-line
    }, []);

    /**
     * Restores state when the selected application tab changes.
     */
    useEffect(() => {
        const newId = M_State.filter(obj => obj.key === 'selectedTab')[0]?.value;

        if (newId !== id) {
            setId(newId);
            getSessionStorageData(newId);
        }
    });

    /**
     * Persists the current tab state whenever editable state changes.
     */
    useEffect(() => {
        const tempArray = [];

        sessionKeys.forEach((key, index) => {
            tempArray.push(sessionStorageMachine(id, key, getSessionKeys()[index]));
        });

        dispatch(M_StateActions.addM_State({ key: id, value: tempArray }));
        // eslint-disable-next-line
    }, [id, operation, roleName, permissions, passLine]);

    //endregion

    //region Data loading

    /**
 * Loads role/profile permissions from the backend.
 *
 * In Create mode, the backend is called with id 0 so the permission grid can be
 * initialized with all available modules.
 *
 * Refresh behavior:
 * - Edit/View/Copy reload from the backend using the selected profile id.
 * - Create reloads the empty grid from id 0.
 * - Role name and permissions are both replaced with backend data.
 *
 * @param {object|string|null|undefined} line - Selected role/profile row.
 * @returns {void}
 */
    function getRefresh(line) {
        const lineRole = line || passLine;

        if (lineRole === 'Error' && !isCreateMode) {
            handleTabClose(id);
            toastMessages('error', '', t("SelectedLine"));
            return;
        }

        const profileId = isCreateMode
            ? 0
            : lineRole?.Id || 0;

        ctx.setIsLoad(true);

        axios.get(`${DefaultURl}profiles/${profileId}/grid`)
            .then(response => {
                const payload = response.data;

                if (isFailurePayload(payload)) {
                    ctx.setIsLoad(false);
                    handleTabClose(id);
                    showApiError(payload, 'ErrorLoadingList');
                    return;
                }

                const profileData = getApiData(payload);

                if (isCreateMode) {
                    setRoleName('');
                } else {
                    setRoleName(profileData?.Name ?? lineRole?.Name ?? '');
                }

                setPermissions(profileData?.Permissions || []);
                ctx.setIsLoad(false);
            })
            .catch(error => {
                ctx.setIsLoad(false);
                handleTabClose(id);
                showApiError(error, 'ErrorLoadingList');
            });
    }

    //endregion

    //region Permission grid

    /**
     * Renders the translated module name for the permission grid.
     *
     * The original ModuleName value is kept unchanged in state because it belongs
     * to the backend DTO and can still be useful for payloads or diagnostics.
     *
     * @param {object} row - Permission row.
     * @returns {string} Translated module display name.
     */
    const moduleNameBody = (row) => {
        const moduleName = row?.ModuleName ?? '';

        return t(moduleName, { defaultValue: moduleName });
    };

    /**
     * Renders a permission checkbox cell.
     *
     * Checkboxes are disabled in View mode to prevent permission changes while viewing
     * a profile.
     *
     * @param {object} row - Permission row.
     * @param {string} field - Permission field name.
     * @returns {JSX.Element} Permission checkbox cell.
     */
    const checkboxBody = (row, field) => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <MCheckBox
                    disabled={isViewMode}
                    checked={row[field] || false}
                    onChange={(elem) => changeTable(row.ModuleId, field, elem.target.checked)}
                    sx={{
                        padding: 0,
                        color: ctx.primaryColor,
                        '&.Mui-checked': {
                            color: ctx.primaryColor,
                        },
                    }}
                />
            </div>
        );
    };

    /**
     * Updates a permission value in the permissions grid.
     *
     * Updates are ignored in View mode as a second layer of protection in addition
     * to disabled UI controls.
     *
     * @param {number} moduleId - Module identifier of the permission row.
     * @param {string} field - Permission field to update.
     * @param {boolean} value - New permission value.
     * @returns {void}
     */
    const changeTable = (moduleId, field, value) => {
        if (isViewMode)
            return;

        setPermissions(permissions.map(obj => {
            if (obj.ModuleId === moduleId) {
                return {
                    ...obj,
                    [field]: value
                };
            }

            return obj;
        }));
    };

    //endregion

    //region Save

    /**
     * Handles a successful save response.
     *
     * The method also supports API response wrappers that return Succeeded=false
     * inside a successful HTTP status.
     *
     * @param {*} payload - Raw API response payload.
     * @returns {void}
     */
    const handleSaveSuccess = (payload) => {
        if (isFailurePayload(payload)) {
            ctx.setIsLoad(false);
            showApiError(payload, 'Error');
            return;
        }

        ctx.setIsLoad(false);
        toastMessages('success', '', t('Success'));

        setTimeout(() => {
            handleTabClose(id);
        }, 1000);
    };

    /**
     * Saves role/profile changes to the backend.
     *
     * Save is available only in Create, Copy, and Edit modes. View mode is blocked
     * at function level so no save can occur from keyboard shortcuts, stale handlers,
     * or unexpected UI paths.
     *
     * @returns {void}
     */
    const onSave = () => {
        if (isViewMode)
            return;

        ctx.setIsLoad(true);

        const tempObj = {
            id: passLine?.Id || 0,
            name: roleName,
            permissions
        };

        if (isCreateMode || isCopyMode) {
            axios.post(`${DefaultURl}Profiles/Create/Grid`, tempObj)
                .then(response => {
                    handleSaveSuccess(response.data);
                })
                .catch(error => {
                    ctx.setIsLoad(false);
                    showApiError(error, 'Error');
                });

            return;
        }

        if (isEditMode) {
            axios.put(`${DefaultURl}Profiles/Update/Grid`, tempObj)
                .then(response => {
                    handleSaveSuccess(response.data);
                })
                .catch(error => {
                    ctx.setIsLoad(false);
                    showApiError(error, 'Error');
                });
        }
    };

    //endregion

    //region Render

    return (
        <>
            <MHeader
                element={
                    <>
                        <MButton
                            tooltip={t("RefreshTable")}
                            icon={<i className="pi pi-refresh headerIcon"></i>}
                            label={t("RefreshTable")}
                            onClick={() => getRefresh()}
                        />

                        {!isViewMode && (
                            <MButton
                                tooltip={t("Save")}
                                icon={<i className="pi pi-save headerIcon"></i>}
                                label={t("Save")}
                                onClick={() => onSave()}
                            />
                        )}
                    </>
                }
            />
            <div style={{ padding: '20px' }}>
                <div className='firstDivContainer'>
                    <div
                        style={{
                            marginBottom: '20px',
                            padding: '20px',
                            borderRadius: '8px',
                        }}
                    >
                        <div className='tableDiv'>
                            <div style={{ width: '250px' }}>
                                <MInputTextField
                                    disabled={isViewMode}
                                    label={t("LabelRoleName")}
                                    value={roleName}
                                    onChange={(e) => setRoleName(e.target.value)}
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                marginTop: '15px'
                            }}
                        >
                            <DataTable
                                value={permissions}
                                dataKey="ModuleId"
                                tableStyle={{ minWidth: '70rem' }}
                            >
                                <Column
                                    field="ModuleName"
                                    header={t("Modules")}
                                    body={moduleNameBody}
                                />

                                {PERMISSION_COLUMNS.map((field) => (
                                    <Column
                                        key={field}
                                        field={field}
                                        header={t(field)}
                                        body={(row) => checkboxBody(row, field)}
                                        style={{ textAlign: 'center' }}
                                    />
                                ))}
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    //endregion
}

//endregion

//region PropTypes

RoleConfigCrud.propTypes = {
    toastMessages: PropTypes.func,
    handleTabClose: PropTypes.func,
};

//endregion